import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import classNames from 'classnames'
import { useState } from 'react'
import { type icons } from '@opallabs/constellation'
import { Size } from '@opallabs/gem'
import { DropdownMenu, Icon } from '@opallabs/lapidary'
import { type CalendarFilterMode } from '../types'
import { FILTER_MODES } from '../valueTree'
import * as styles from './styles'

type ModeCopy = Readonly<{
  glyph: keyof typeof icons.gem
  /** What choosing this mode does with everything checked under it. */
  hint: string
  label: string
}>

const MODES: Record<CalendarFilterMode, ModeCopy> = {
  exclude: {
    glyph: 'gem-minus-circle-filled',
    hint: 'Exclude selected items',
    label: 'Exclude',
  },
  include: {
    glyph: 'gem-add-circle-filled',
    hint: 'Include selected items',
    label: 'Include',
  },
}

/**
 * The plus or the minus that stands for a mode, in the colour that means it.
 *
 * It is styled on the element rather than from the drawer's own stylesheet
 * because the menu below is drawn outside the drawer, where a rule that only
 * reaches inside it would not land.
 */
export const ModeMark: React.FC<{
  mode: CalendarFilterMode
  /** 16px on a control; 12px on the heading over a group of tokens. */
  size?: number
}> = ({ mode, size = Size.Icon }) => (
  <span css={styles.modeMark(mode)}>
    <Icon
      ariaHidden={true}
      color="currentColor"
      glyph={MODES[mode].glyph}
      size={size}
    />
  </span>
)
ModeMark.displayName = 'Calendar: ModeMark'

export type ModeSelectProps = Readonly<{
  /**
   * What this control speaks for, said after the mode. A tier of label sets
   * carries one of these on every row, and "Include" alone names all of them.
   */
  context?: string
  /**
   * Names the radio group and the hint it is described by. Two of these on one
   * page would otherwise share a group, and picking in either would move both.
   */
  htmlName: string
  /** The row-sized dropdown: the mark, the word and the chevron, and no hint. */
  isCompact?: boolean
  /**
   * Shown but not offered: the tier under one channel ticks in the mode of the
   * whole category, which is set a pane back where every channel is in view.
   * The control stays where the design puts it, dimmed, so the pane still says
   * which way round its ticks are read.
   */
  isDisabled?: boolean
  mode: CalendarFilterMode
  onChange: (mode: CalendarFilterMode) => void
}>

/**
 * Whether what this speaks for matches what it holds or hides it.
 *
 * It turns every value already ticked under it around with it, so it belongs
 * wherever the whole of what it speaks for is in front of a person: over a
 * category that makes the choice once, and on the row of a label set, which
 * makes it for itself.
 */
export const ModeSelect: React.FC<ModeSelectProps> = ({
  context,
  htmlName,
  isCompact = false,
  isDisabled = false,
  mode,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const hintId = `${htmlName}-hint`

  const menu = (
    <DropdownMenu.Root onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenu.Trigger
        aria-describedby={isCompact ? undefined : hintId}
        className={classNames(styles.selectors.modeTrigger, {
          [styles.selectors.modeTriggerCompact]: isCompact,
        })}
        disabled={isDisabled}
      >
        <ModeMark mode={mode} />
        <span className={styles.selectors.text}>{MODES[mode].label}</span>
        {context === undefined ? null : (
          <VisuallyHidden> mode for {context}</VisuallyHidden>
        )}
        <Icon ariaHidden={true} glyph="gem-chevron-s" size={Size.Icon} />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start">
          {/*
            `useChildren` draws each row as it is written here rather than as
            a lapidary radio input. That is what the design asks for, and it
            is what lets the menu close on a pick: an input of its own would
            still be settling as the menu around it went away.
          */}
          <DropdownMenu.RadioGroup
            name={htmlName}
            onValueChange={(value) => {
              onChange(value as CalendarFilterMode)
              // A lapidary menu holds itself open on every pick. That is
              // right for the menus that set several things at once, and
              // wrong here: there is one choice, and it has been made.
              setIsOpen(false)
            }}
            useChildren={true}
            value={mode}
          >
            {FILTER_MODES.map((value) => (
              <DropdownMenu.RadioItem
                key={value}
                css={styles.modeOption}
                useChildren={true}
                value={value}
              >
                <ModeMark mode={value} />
                <span className={styles.selectors.text}>
                  {MODES[value].label}
                </span>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )

  if (isCompact) return menu

  return (
    <div className={styles.selectors.modeSelect}>
      {menu}
      <span className={styles.selectors.modeHint} id={hintId}>
        {MODES[mode].hint}
      </span>
    </div>
  )
}
ModeSelect.displayName = 'Calendar: ModeSelect'

/**
 * The heading over one group of tokens on the category list, saying what the
 * tokens under it do.
 *
 * It is what lets a token drop the word "Not": the group says it once for all
 * of them, which is the only way a category holding a matched set beside a
 * hidden one can be read at a glance.
 */
export const ModeGroupLabel: React.FC<{ mode: CalendarFilterMode }> = ({
  mode,
}) => (
  <div className={styles.selectors.modeGroupLabel}>
    <ModeMark mode={mode} size={Size.SmallIcon} />
    <span className={styles.selectors.text}>{MODES[mode].label}</span>
  </div>
)
ModeGroupLabel.displayName = 'Calendar: ModeGroupLabel'
